import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Container,
  Typography,
  Box,
  Grid,
  TextField,
  Button,
  Paper,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  CircularProgress,
  Alert,
  styled,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import ReCAPTCHA from "react-google-recaptcha";
import PageLayout from "../components/Common/PageLayout";
import PageBanner from "../components/Common/PageBanner";
import {
  updateNullOfObjectValues,
  isEmail,
  isPhoneNumber,
  generateUniqueToken,
} from "../utils/helper";
import { contactUsSubmit } from "../actions/pagecontent.actions";
import { getIpAddress } from "@smfa/utils";

// Styled components
const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
  "& .MuiDialog-paper": {
    borderRadius: "8px",
  },
}));

/**
 * Contact page component
 * @returns {JSX.Element}
 * @component
 */
const Contact = () => {
  const navigate = useNavigate();
  const recaptchaRef = useRef(null);
  const [loader, setLoader] = useState(true);
  const [open, setOpen] = useState(false);
  const [ipAddress, setIpAddress] = useState("0.0.0.0");

  // Extract state data with safe access patterns
  const { home: homeData, contactus: contactusData } =
    useSelector((state) => state.pageContents) || {};

  // Form state
  const formFields = {
    name: "",
    email: "",
    phone_number: "",
    message: "",
    recaptcha: "",
  };
  const [formValues, setFormValues] = useState(formFields);
  const [formErrors, setFormErrors] = useState(
    updateNullOfObjectValues(formFields),
  );
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  // Initialize loading state
  useEffect(() => {
    // Fetch IP address
    const _getIp = async () => {
      setIpAddress(await getIpAddress());
    };
    _getIp();

    const timer = setTimeout(() => setLoader(false), 1500);
    log.debug("generateUniqueToken", generateUniqueToken());
    return () => clearTimeout(timer);
  }, []);

  // Form handling functions
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormValues({ ...formValues, [name]: value });
    setFormErrors({ ...formErrors, [name]: null });
  };

  const validateForm = () => {
    let isValid = true;
    let errors = {};

    // Validate required fields
    Object.keys(formValues).forEach((key) => {
      if (!formValues[key] && key !== "recaptcha") {
        isValid = false;
        errors[key] = "This field is required";
      } else if (key === "email" && !isEmail(formValues[key])) {
        isValid = false;
        errors[key] = "Invalid email address";
      } else if (key === "phone_number" && !isPhoneNumber(formValues[key])) {
        isValid = false;
        errors[key] = "Invalid phone number";
      }
    });

    // Validate reCAPTCHA
    const recaptchaValue = recaptchaRef.current?.getValue();
    if (!recaptchaValue) {
      isValid = false;
      errors.recaptcha = "Please verify captcha";
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (submitting) return;

    if (validateForm()) {
      setSubmitting(true);
      setErrorMessage("");
      setSuccessMessage("");

      let formData = {
        ...formValues,
        twitter_link: homeData?.data?.widgetdata?.footer_twitter_link,
        ip_address: ipAddress || "0.0.0.0",
      };
      formData.recaptcha = recaptchaRef.current.getValue();

      contactUsSubmit(formData)
        .then((response) => {
          setSubmitting(false);
          if (response.data.status) {
            setFormValues(formFields);
            recaptchaRef.current.reset();
            setOpen(true);
          } else {
            setErrorMessage(response.data.message);
          }
        })
        .catch((error) => {
          setSubmitting(false);
          setErrorMessage("Something went wrong. Please try again later.");
        });
    }
  };

  const handleClose = () => setOpen(false);
  const handleRegister = () => navigate("/signup");

  return (
    <PageLayout pageName="Contact Us" showLoader={loader} checkComingSoon>
      {/* Banner */}
      {contactusData?.data?.contactbanner ? (
        <PageBanner
          bannerImage={contactusData.data.contactbanner.banner_image}
          mainTitle={
            contactusData.data.contactbanner.banner_title || "Contact Us"
          }
          pageTitle="Contact"
        />
      ) : (
        <PageBanner mainTitle="Contact Us" pageTitle="Contact" />
      )}

      {/* Content Section */}
      <Box component="section" sx={{ py: 8 }}>
        <Container>
          {/* Section Header */}
          {contactusData?.data?.contactquery && (
            <Box sx={{ mb: 5, textAlign: "center" }}>
              <Typography
                variant="subtitle1"
                color="primary"
                gutterBottom
                component="span"
              >
                {contactusData.data.contactquery.section_title}
              </Typography>
              <Typography variant="h4" component="h2" gutterBottom>
                {contactusData.data.contactquery.section_sub_title}
              </Typography>
              <Box
                component="div" // Use a div or another suitable element
                sx={{ color: "text.secondary" }} // Apply styling as needed
                dangerouslySetInnerHTML={{
                  __html: contactusData.data.contactquery.section_description,
                }}
              />
            </Box>
          )}

          {/* Contact Form */}
          <Paper
            elevation={2}
            sx={{
              borderRadius: 2,
              p: { xs: 2, sm: 4 },
              maxWidth: "900px",
              mx: "auto",
            }}
          >
            <Box component="form" onSubmit={handleSubmit} noValidate>
              <Grid container spacing={3}>
                {/* Name Field */}
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    label="Name"
                    name="name"
                    value={formValues.name}
                    onChange={handleInputChange}
                    error={!!formErrors.name}
                    helperText={formErrors.name || ""}
                    variant="outlined"
                    required
                  />
                </Grid>

                {/* Email Field */}
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    label="Email"
                    name="email"
                    type="email"
                    value={formValues.email}
                    onChange={handleInputChange}
                    error={!!formErrors.email}
                    helperText={formErrors.email || ""}
                    variant="outlined"
                    required
                  />
                </Grid>

                {/* Phone Field */}
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    name="phone_number"
                    value={formValues.phone_number}
                    onChange={handleInputChange}
                    error={!!formErrors.phone_number}
                    helperText={formErrors.phone_number || ""}
                    variant="outlined"
                    required
                  />
                </Grid>

                {/* Message Field */}
                <Grid size={12}>
                  <TextField
                    fullWidth
                    label="Message"
                    name="message"
                    value={formValues.message}
                    onChange={handleInputChange}
                    error={!!formErrors.message}
                    helperText={formErrors.message || ""}
                    variant="outlined"
                    required
                    multiline
                    rows={6}
                  />
                </Grid>

                {/* reCAPTCHA */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box sx={{ mt: 1 }}>
                    <ReCAPTCHA
                      ref={recaptchaRef}
                      sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                    />
                    {formErrors.recaptcha && (
                      <Typography
                        color="error"
                        variant="caption"
                        sx={{ mt: 1, display: "block" }}
                      >
                        {formErrors.recaptcha}
                      </Typography>
                    )}
                  </Box>
                </Grid>

                {/* Submit Button */}
                <Grid
                  size={{ xs: 12, md: 6 }}
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "flex-end",
                  }}
                >
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    size="large"
                    disabled={submitting}
                    sx={{ mt: 2 }}
                  >
                    {submitting ? (
                      <>
                        <CircularProgress
                          size={20}
                          color="inherit"
                          sx={{ mr: 1 }}
                        />
                        Sending...
                      </>
                    ) : (
                      "Send Message"
                    )}
                  </Button>
                </Grid>

                {/* Success/Error Messages */}
                {(successMessage || errorMessage) && (
                  <Grid size={12}>
                    {successMessage && (
                      <Alert severity="success" sx={{ mt: 2 }}>
                        {successMessage}
                      </Alert>
                    )}
                    {errorMessage && (
                      <Alert severity="error" sx={{ mt: 2 }}>
                        {errorMessage}
                      </Alert>
                    )}
                  </Grid>
                )}
              </Grid>
            </Box>
          </Paper>
        </Container>
      </Box>

      {/* Thank You Dialog */}
      <StyledDialog onClose={handleClose} open={open} maxWidth="md">
        <DialogTitle sx={{ m: 0, p: 2 }}>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3} alignItems="center">
            <Grid size={{ xs: 12, md: 5 }}>
              <Box
                component="img"
                src={
                  process.env.REACT_APP_IMAGE_URL + "/1719212972_doctor11.png"
                }
                alt="Thank you"
                sx={{
                  width: "100%",
                  display: "block",
                  maxHeight: "300px",
                  objectFit: "contain",
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 7 }}>
              <Box textAlign="center">
                <Typography variant="h4" gutterBottom>
                  Thank You!
                </Typography>
                <Typography variant="body1">
                  We will get back to you soon
                </Typography>
                <Typography variant="body1">
                  While you wait, why not register with us
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleRegister}
                  sx={{ mt: 2 }}
                >
                  Register Now
                </Button>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
      </StyledDialog>
    </PageLayout>
  );
};

export default Contact;
